import { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * Formly types this extension applies to. Only leaf, text-entry-like input types where a
 * browser/password manager can plausibly offer autofill. `textarea` is deliberately excluded —
 * free-text notes are never autofill targets. There is no `phone`/`custom-phone` type in
 * `INPUT_OPTIONS` (field-config.ts) at the time of writing, so only `input` and the custom
 * `email` type are covered.
 */
export const AUTOCOMPLETE_TYPES = new Set(['input', 'email']);

/**
 * Field-key (not keyPath) to autocomplete-token map, keyed lowercase for case-insensitive
 * lookup. Covers the common DistroDot name/contact field keys (Dutch and English spellings).
 * See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete for the token
 * vocabulary.
 */
export const AUTOCOMPLETE_BY_KEY: Record<string, string> = {
  firstname: 'given-name',
  voornaam: 'given-name',
  voornamen: 'given-name',
  lastname: 'family-name',
  achternaam: 'family-name',
  email: 'email',
  'e-mail': 'email',
  'e-mailadres': 'email',
  emailadress: 'email',
  phone: 'tel',
  telefoon: 'tel',
  telefoonnummer: 'tel',
  mobiel: 'tel',
  'mobiel-nummer': 'tel',
  mobile: 'tel',
  'company-name': 'organization',
  bedrijfsnaam: 'organization',
  postcode: 'postal-code',
  'postal-code': 'postal-code',
  postalcode: 'postal-code',
  streetname: 'address-line1',
  city: 'address-level2',
  plaats: 'address-level2',
  woonplaats: 'address-level2',
};

/**
 * Suggests an `autocomplete` token for a field key. This is a **form-builder placeholder
 * suggestion helper only** — it is no longer applied on the runtime path (see
 * `autocompleteExtension` below). It backs the "Autocomplete-hint" property panel's placeholder
 * preview (form-builder.component.ts), so an admin sees a sensible default token to type in
 * without the runtime silently guessing from unrelated field keys.
 * Pure function.
 *
 * @param key - the field's own `key` (not the dotted keyPath) — only a plain string is looked
 *   up, a numeric or missing key never matches.
 * @param override - a configured value; wins over derivation when non-empty.
 */
export function deriveAutocomplete(key: string | number | undefined, override?: string): string | undefined {
  if (override) {
    return override;
  }
  if (typeof key !== 'string' || !key) {
    return undefined;
  }
  return AUTOCOMPLETE_BY_KEY[key.toLowerCase()];
}

/**
 * Stamps a browser/password-manager `autocomplete` attribute on name/contact-shaped leaf
 * inputs (bug #21298: those inputs carried no autocomplete hint at all, so autofill never
 * recognised them).
 *
 * Precedence:
 * 1. An already-set `props.attributes.autocomplete` (manually configured, or set by a field
 *    component of its own accord) is left untouched.
 * 2. `props.autocomplete` — an explicit config value, either the form-builder
 *    "Autocomplete-hint" property or a composite field definition (NAME_FIELDS, ADDRESS_FIELDS
 *    in fieldGroup.config.ts) that sets it up front — wins when set. `'off'` propagates too.
 *
 * There is deliberately no key-name fallback here: guessing from an arbitrary field's `key`
 * produced false positives on unrelated fields that merely happened to share a key name.
 * `deriveAutocomplete` still offers that heuristic, but only as a form-builder placeholder
 * suggestion — never applied automatically at runtime.
 *
 * Writes nothing when no token applies — never falls back to the generic `autocomplete="on"`.
 */
export function autocompleteExtension(field: FormlyFieldConfig): void {
  if (!field.props || field.fieldGroup || !field.type || typeof field.type !== 'string') {
    return;
  }

  if (!AUTOCOMPLETE_TYPES.has(field.type)) {
    return;
  }

  if (field.props.attributes?.['autocomplete']) {
    return;
  }

  const token = deriveAutocomplete(undefined, field.props['autocomplete']);

  if (!token) {
    return;
  }

  field.props.attributes = {
    ...field.props.attributes,
    autocomplete: token,
  };
}
